import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Lightning } from "@phosphor-icons/react";

/**
 * Generic Prospect Playbook Renderer.
 *
 * Fetches a markdown file from /api/prospect-doc/{slug} and renders it
 * with FlashGrants typography + print-optimized layout.
 *
 * Route: /playbook/doc/:slug
 *
 * Example: /playbook/doc/charles_campbell_plan
 *
 * Greg's flow:
 *   1. Open the URL on a laptop or share directly via email/text
 *   2. Reader sees a polished page (not raw .md)
 *   3. Cmd/Ctrl + P → Save as PDF → archive if needed
 */
const PlaybookDoc = () => {
  const { slug } = useParams();
  const [content, setContent] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDoc = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${backendUrl}/api/prospect-doc/${slug}`);
        if (!res.ok) {
          setError(`Document not found: ${slug}`);
          setLoading(false);
          return;
        }
        const text = await res.text();
        setContent(text);
        setLoading(false);
      } catch (e) {
        setError(`Failed to load document: ${e.message}`);
        setLoading(false);
      }
    };
    fetchDoc();
  }, [slug]);

  // Extract H1 from markdown content for header display (first # line)
  const h1Match = content.match(/^#\s+(.+)$/m);
  const docTitle = h1Match ? h1Match[1] : "Plan";
  // Body content with H1 stripped (we render it in the header band)
  const body = h1Match ? content.replace(/^#\s+.+\n?/m, "") : content;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="playbook-doc-loading">
        <div className="text-zinc-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="playbook-doc-error">
        <div className="text-zinc-700 text-base">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900 print:bg-white" data-testid="playbook-doc-page">
      {/* ─── FLOATING PRINT/PDF BUTTON ─── */}
      <button
        onClick={() => window.print()}
        data-testid="playbook-print-btn"
        className="fixed top-4 right-4 z-50 bg-emerald-800 hover:bg-emerald-700 text-white font-bold text-sm px-4 py-2.5 rounded-md shadow-lg print:hidden transition-colors flex items-center gap-2"
      >
        <span>Print / Save PDF</span>
      </button>

      {/* ─── HEADER BAND ─── */}
      <header className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 text-white px-6 sm:px-12 py-10 print:px-10 print:py-8">
        <div className="max-w-[820px] mx-auto">
          <div className="flex items-center gap-2.5 mb-8">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-300" />
            <span className="font-bold tracking-tight text-xl">FlashGrants</span>
          </div>
          <div className="text-emerald-300 font-bold tracking-[0.18em] text-xs uppercase mb-3">
            Personalized Founder Plan
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
            {docTitle}
          </h1>
        </div>
      </header>

      {/* ─── BODY ─── */}
      <article
        className="max-w-[820px] mx-auto px-6 sm:px-12 py-12 print:px-10 print:py-8"
        data-testid="playbook-doc-body"
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => (
              <h1 className="text-3xl sm:text-4xl font-black text-zinc-900 mt-12 mb-6 leading-tight">
                {children}
              </h1>
            ),
            h2: ({ children }) => (
              <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 mt-12 mb-4 leading-tight border-b border-zinc-200 pb-2">
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="text-xl sm:text-2xl font-bold text-zinc-900 mt-8 mb-3 leading-snug">
                {children}
              </h3>
            ),
            p: ({ children }) => (
              <p className="text-zinc-800 leading-relaxed text-lg mb-4">{children}</p>
            ),
            strong: ({ children }) => (
              <strong className="font-bold text-zinc-900">{children}</strong>
            ),
            em: ({ children }) => <em className="italic text-zinc-800">{children}</em>,
            ul: ({ children }) => (
              <ul className="list-disc pl-6 space-y-2 mb-4 text-zinc-800 leading-relaxed">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="list-decimal pl-6 space-y-2 mb-4 text-zinc-800 leading-relaxed">
                {children}
              </ol>
            ),
            li: ({ children }) => <li className="text-lg leading-relaxed">{children}</li>,
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 font-semibold underline hover:text-emerald-900 break-words"
              >
                {children}
              </a>
            ),
            hr: () => <hr className="my-10 border-t border-zinc-300" />,
            blockquote: ({ children }) => (
              <blockquote className="border-l-4 border-emerald-500 bg-emerald-50 text-emerald-950 p-4 rounded-r my-4 text-lg italic">
                {children}
              </blockquote>
            ),
            code: ({ inline, children }) =>
              inline ? (
                <code className="bg-zinc-100 text-zinc-800 px-1.5 py-0.5 rounded text-sm font-mono">
                  {children}
                </code>
              ) : (
                <pre className="bg-zinc-900 text-zinc-100 p-4 rounded my-4 overflow-x-auto text-sm font-mono">
                  <code>{children}</code>
                </pre>
              ),
            table: ({ children }) => (
              <div className="overflow-x-auto my-6 -mx-2 sm:mx-0">
                <table className="min-w-full border-collapse border border-zinc-300 text-base">
                  {children}
                </table>
              </div>
            ),
            thead: ({ children }) => <thead className="bg-emerald-900 text-white">{children}</thead>,
            tbody: ({ children }) => <tbody className="bg-white">{children}</tbody>,
            tr: ({ children }) => <tr className="border-b border-zinc-200">{children}</tr>,
            th: ({ children }) => (
              <th className="border border-zinc-300 px-3 py-2 text-left font-bold tracking-wide text-sm uppercase">
                {children}
              </th>
            ),
            td: ({ children }) => (
              <td className="border border-zinc-300 px-3 py-2 align-top text-zinc-800 leading-relaxed">
                {children}
              </td>
            ),
            img: ({ src, alt }) => (
              <figure className="my-8">
                <img
                  src={src}
                  alt={alt || ""}
                  loading="lazy"
                  className="w-full h-auto rounded-lg border border-zinc-200 shadow-sm bg-zinc-50"
                />
                {alt && (
                  <figcaption className="mt-2 text-sm text-zinc-500 italic text-center">
                    {alt}
                  </figcaption>
                )}
              </figure>
            ),
          }}
        >
          {body}
        </ReactMarkdown>

        {/* ─── FOOTER ─── */}
        <footer className="mt-16 pt-8 border-t border-zinc-200 text-center text-zinc-500 text-sm print:mt-12">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Lightning weight="fill" className="w-4 h-4 text-emerald-600" />
            <span className="font-bold text-zinc-700">FlashGrants</span>
          </div>
          <div>flash-grants.com · Myrtle Beach, SC</div>
        </footer>
      </article>
    </div>
  );
};

export default PlaybookDoc;
